import type { RuleBoxFacets, RuleBoxLocationChoice } from "@loot-os/api-contract";

import { ScopeGate } from "./ScopeGate";

/**
 * The fulfillment filter: the shop's locations, its own and its fulfillment apps', as checkboxes.
 * A product qualifies when it is stocked at a checked one; none checked is any location. Naming
 * them needs `read_locations`, which the merchant grants here. A checked location the shop no
 * longer has stays listed, so it can be unchecked.
 */
export function LocationChoices({
  facets,
  chosen,
  pending,
  onAllow,
  onChange,
}: {
  facets: RuleBoxFacets;
  chosen: string[];
  pending: boolean;
  onAllow: () => void;
  onChange: (next: string[]) => void;
}) {
  if (!facets.locationsNamed && facets.locations.length === 0) {
    return (
      <ScopeGate pending={pending} onAllow={onAllow}>
        Fill the box only from products stocked at chosen locations: your own, or a fulfillment
        app's like ShipBob. Loot Boxes needs access to your locations to list them.
      </ScopeGate>
    );
  }
  if (facets.locations.length === 0 && chosen.length === 0) {
    // Every store has a location, so none named means naming them has not worked yet.
    return (
      <ScopeGate pending={pending} action="Try again" onAllow={onAllow}>
        We couldn't list your locations yet.
      </ScopeGate>
    );
  }
  const listed = new Set(facets.locations.map((l) => l.id));
  const gone: RuleBoxLocationChoice[] = chosen
    .filter((id) => !listed.has(id))
    .map((id) => ({ id, label: "Closed location", fulfillmentService: null }));
  const locations = [...facets.locations, ...gone];
  return (
    <s-choice-list
      label="Locations"
      name="locations"
      multiple
      details="Only products stocked at one of these locations go in. Leave all unchecked for any location. Shopify still picks which location ships each order."
      onChange={(e: { currentTarget: EventTarget }) =>
        onChange([...((e.currentTarget as unknown as { values: string[] }).values ?? [])])
      }
    >
      {locations.map((location) => (
        <s-choice
          key={location.id}
          value={location.id}
          selected={chosen.includes(location.id) || undefined}
        >
          {location.label}
          {location.fulfillmentService && (
            <s-text slot="details">Fulfillment app: {location.fulfillmentService}</s-text>
          )}
        </s-choice>
      ))}
    </s-choice-list>
  );
}
